import { useCounterpartiesQueryData } from '@src/providers/Counterparties/useCounterpartiesQueryData';
import type { OTCDealData } from '@src/types/OTC';
import {
  CUSTOMER_MARGIN,
  DEAL,
  DealStatusEnum,
  SUB_ACCOUNT_COLLATERAL_STATUS,
  SubProductTypeEnum,
  useBehaviorSubject,
  useCombinedErrorObs,
  useSecuritiesContext,
  useSubscription,
  type CustomerMargin,
  type ISubAccountCollateralStatus,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { createCounterpartyMonitoringDataObservable } from './counterpartyMonitoringObsUtils';

export function useCounterpartyMonitoringObs() {
  const { securitiesBySymbolObs } = useSecuritiesContext();
  const { counterpartiesByName } = useCounterpartiesQueryData();
  // Keep row memory stable while still re-normalizing existing rows when lookup data changes.
  const { observable: counterpartiesByNameObs } = useBehaviorSubject(
    () => counterpartiesByName,
    [counterpartiesByName]
  );
  const { data: customerMarginData, error: customerMarginError } = useSubscription<CustomerMargin>({
    name: CUSTOMER_MARGIN,
    tag: 'Counterparty Monitoring - CustomerMargin',
  });
  const { data: dealData, error: dealError } = useSubscription<OTCDealData>({
    name: DEAL,
    tag: 'Counterparty Monitoring - OTC Deals',
    sort_by: '-TradeTime',
    Statuses: [DealStatusEnum.Live],
    SubProductTypes: [
      SubProductTypeEnum.OTCFixedRateLoan,
      SubProductTypeEnum.OTCFloatingRateLoan,
      SubProductTypeEnum.OTCRepo,
    ],
  });
  const { data: subAccountCollateralStatusData, error: subAccountCollateralStatusError } =
    useSubscription<ISubAccountCollateralStatus>({
      name: SUB_ACCOUNT_COLLATERAL_STATUS,
      tag: 'Counterparty Monitoring - SubAccountCollateralStatus',
    });

  const counterpartyMonitoringRowsObservable = useMemo(
    () =>
      createCounterpartyMonitoringDataObservable({
        customerMarginData,
        dealData,
        subAccountCollateralStatusData,
        securitiesBySymbol: securitiesBySymbolObs,
        counterpartiesByName: counterpartiesByNameObs,
      }),
    [counterpartiesByNameObs, customerMarginData, dealData, securitiesBySymbolObs, subAccountCollateralStatusData]
  );

  const errorObservable = useCombinedErrorObs([customerMarginError, dealError, subAccountCollateralStatusError]);

  return { counterpartyMonitoringRowsObservable, errorObservable };
}
